/**
 * Search / Go To — find a place, a port, a charted object, or type a position,
 * then tap it to centre the chart there and drop a go-to marker. Free-text
 * matches the named places and objects on the loaded chart (and our own marks and
 * routes); a typed position is read in degrees, minutes and decimal minutes, the
 * way the charts and RYA courses work.
 *
 * It reads the real stores: the chart's named places (published to the search
 * index by the map), and our marks and routes from the routing store. Going to a
 * result reuses the existing go-to machinery, so the data fields read the bearing
 * and distance to it live, just like a "go to mark" from the chart.
 */
import { useEffect, useRef, useState } from 'react';
import { useShellStore } from '@/store/shell';
import { useChartPlotterStore } from '@/store';
import { useRoutingStore, nextWaypointId } from '@/store/routing';
import { useSearchIndexStore } from '@/store/searchIndex';
import { buildSearchResults, type SearchResult } from './searchSources';
import { setDestinationAndOfferRoute } from './setDestinationFlow';
import { Sheet } from './Sheet';

const KIND_GLYPH: Record<SearchResult['kind'], string> = {
  latlon: '🎯',
  place: '📍',
  object: '⚓',
  mark: '🚩',
  route: '🧭',
};

/** A sensible zoom for landing on each kind of hit. */
function zoomFor(kind: SearchResult['kind']): number {
  if (kind === 'place') return 12.5;
  if (kind === 'route') return 11.5;
  return 13.8;
}

export function SearchSheet() {
  const activeSheet = useShellStore((s) => s.activeSheet);
  const open = activeSheet === 'search';
  const setFocus = useShellStore((s) => s.setFocus);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);

  const places = useSearchIndexStore((s) => s.places);
  const marks = useRoutingStore((s) => s.marks);
  const routes = useRoutingStore((s) => s.routes);
  const ownship = useChartPlotterStore((s) => s.ownship.position);

  const [query, setQuery] = useState('');
  // The result the user tapped, waiting on a choice (centre / set destination /
  // go to). null = showing the list. We hold off on any action until they pick,
  // so selecting a place no longer fires a go-to on its own.
  const [chosen, setChosen] = useState<SearchResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Clear and focus the box each time the sheet opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setChosen(null);
      const t = window.setTimeout(() => inputRef.current?.focus(), 60);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  const results = buildSearchResults(query, { places, marks, routes });

  // Close up: clear the chooser and the box, and drop the sheet.
  function finish() {
    setChosen(null);
    setQuery('');
    closeSheet();
  }

  // Tapping a result. A route isn't a single destination, so centre on it and
  // point at the route manager as before. Anything that IS a point (a place,
  // object, mark, or typed position) opens the choice instead of committing.
  function pick(r: SearchResult) {
    if (r.kind === 'route') {
      setFocus(r.position, zoomFor(r.kind));
      showToast(`Centred on ${r.label} — open Route to follow it`);
      finish();
      return;
    }
    setChosen(r);
  }

  // Just pan/zoom the chart there. No destination, no go-to.
  function centreOn(r: SearchResult) {
    setFocus(r.position, zoomFor(r.kind));
    showToast(`Centred on ${r.label}`);
    finish();
  }

  // Set it as the passage destination, which then offers to plot a route to it.
  function setDest(r: SearchResult) {
    setDestinationAndOfferRoute(r.position, r.kind === 'latlon' ? undefined : r.label);
    finish();
  }

  // The old behaviour: aim a live go-to at the point (bearing + distance read live).
  function goToResult(r: SearchResult) {
    const rs = useRoutingStore.getState();
    if (r.kind === 'mark') {
      // Go to the existing mark, don't make a duplicate.
      const markId = r.id.slice('mark:'.length);
      if (ownship) rs.gotoMark(markId, ownship);
      setFocus(r.position, zoomFor(r.kind));
      showToast(ownship ? `Going to ${r.label}` : `Centred on ${r.label}`);
    } else {
      // A place, charted object, or typed position: drop a go-to mark and aim at it.
      const id = nextWaypointId();
      const name = r.kind === 'latlon' ? 'Go to' : r.label;
      rs.addMark({ id, position: r.position, name });
      if (ownship) rs.gotoMark(id, ownship);
      setFocus(r.position, zoomFor(r.kind));
      showToast(ownship ? `Going to ${name}` : `Marked ${name}`);
    }
    finish();
  }

  return (
    <Sheet
      name="search"
      title="Search / Go to"
      sub="Find a place, port, or charted object, or type a position. Tap a result and choose what to do: centre the chart, set it as your destination, or go to it."
    >
      <input
        ref={inputRef}
        className="acp-text-input"
        type="search"
        inputMode="search"
        placeholder="Place, port, object, or 46 12.6 N 005 49.8 W"
        value={query}
        aria-label="Search or type a position"
        onChange={(e) => {
          setQuery(e.target.value);
          setChosen(null);
        }}
        style={{ width: '100%' }}
      />
      <p className="acp-note">
        Type a position as degrees, minutes and decimal minutes with N/S and E/W, e.g.
        46 12.6 N 005 49.8 W. That’s how the charts and RYA courses work.
      </p>

      {chosen ? (
        <div className="acp-goto-choice">
          <p className="acp-note">
            <b>{chosen.label}</b>
            <br />
            {chosen.sublabel}
          </p>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" onClick={() => centreOn(chosen)}>
              Centre
            </button>
            <button type="button" className="acp-btn" onClick={() => setDest(chosen)}>
              Set destination
            </button>
          </div>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn primary full" onClick={() => goToResult(chosen)}>
              Go to
            </button>
          </div>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn full" onClick={() => setChosen(null)}>
              Back to results
            </button>
          </div>
        </div>
      ) : query.trim() === '' ? (
        <p className="acp-note">
          {places.length > 0
            ? `${places.length} charted places and objects to search, plus your marks and routes.`
            : 'Start typing a name, or a position, to search.'}
        </p>
      ) : results.length === 0 ? (
        <p className="acp-note">No matches for “{query.trim()}”. Check the spelling, or type a position.</p>
      ) : (
        <div className="acp-search-results">
          {results.map((r) => (
            <button key={r.id} type="button" className="acp-menu-row" onClick={() => pick(r)}>
              <span className="ic" aria-hidden="true">{KIND_GLYPH[r.kind]}</span>
              <span className="lbl">
                {r.label}
                <span className="sub">{r.sublabel}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </Sheet>
  );
}
